import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";

import AdminLoginPage from "./admin/pages/AdminLoginPage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import PackageManagementPage from "./admin/pages/PackageManagementPage";
import InventoryManagementPage from "./admin/pages/animal/InventoryManagementPage";
import LabourManagementPage from "./admin/pages/LabourManagementPage";
import GoatLabourPage from "./admin/pages/animal/GoatLabourPage";
import BatchManagementPage from "./admin/pages/animal/BatchManagementPage";
import BatchExpensesPage from "./admin/pages/animal/BatchExpensesPage";
import LivestockPage from "./admin/pages/animal/LivestockPage";
import InvestorManagementPage from "./admin/pages/InvestorManagementPage";
import DiseaseManagementPage from "./admin/pages/animal/DiseaseManagementPage";
import FeedingManagementPage from "./admin/pages/animal/FeedingManagementPage";
import VaccineManagementPage from "./admin/pages/animal/VaccineManagementPage";
import VaccineSchedulePage from "./admin/pages/animal/VaccineSchedulePage";
import MedicineManagementPage from "./admin/pages/animal/MedicineManagementPage";
import TaskPage from "./admin/pages/animal/TaskPage";
import EmailReminderPage from "./admin/pages/animal/EmailReminderPage";
import FarmCareUpdatesPage from "./admin/pages/animal/FarmCareUpdatesPage";
import UserRegisterPage from "./user/pages/UserRegisterPage";

// -------------------- Farming Pages --------------------
import FertilizerMasterPage from "./admin/pages/farming/FertilizerMasterPage";
import ZoneMasterPage from "./admin/pages/farming/ZoneMasterPage";
import SchedulePage from "./admin/pages/farming/SchedulePage";
import FarmingTaskPage from "./admin/pages/farming/FarmingTaskPage";
import StockPage from "./admin/pages/farming/StockPage";

const UserLoginPage = () => {
  const shared = require("./shared/components/LoginPage");
  const LoginPage = shared.default;
  return <LoginPage role="user" />;
};

// ─────────────────────────────────────────────
// GLOBAL STYLES
// ─────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --sidebar-w: 240px;
      --bg-main:   #f8fafc;
      --bg-sidebar:#ffffff;
      --border:    #e2e8f0;
      --text-primary: #172334;
      --text-muted:   #64748b;
      --text-dim:     #475569;
      --transition: 0.18s ease;
    }

    body {
      background: var(--bg-main);
      color: var(--text-primary);
      font-family: 'Plus Jakarta Sans', sans-serif;
      -webkit-font-smoothing: antialiased;
    }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; }

    .nav-link {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      border-radius: 10px;
      margin-bottom: 2px;
      text-decoration: none;
      font-size: 13px;
      font-weight: 500;
      color: #475569;
      position: relative;
      transition: all var(--transition);
      overflow: hidden;
    }

    .nav-link:hover {
      color: #166a55;
      background: #eaf6f1;
    }

    .nav-link:hover .nav-icon { opacity: 0.8; }

    .nav-link.active { font-weight: 600; }

    .nav-link.active .nav-icon {
      opacity: 1;
      filter: drop-shadow(0 0 5px currentColor);
    }

    .nav-pill {
      position: absolute;
      inset: 0;
      border-radius: 10px;
      opacity: 0;
      transition: opacity var(--transition);
      pointer-events: none;
    }

    .nav-link.active .nav-pill { opacity: 1; }

    .nav-icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      position: relative;
      z-index: 1;
      opacity: 0.35;
      transition: all var(--transition);
    }

    .nav-label {
      position: relative;
      z-index: 1;
    }

    .nav-section {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #94a3b8;
      padding: 14px 10px 5px;
    }

    .nav-divider {
      height: 1px;
      background: #e2e8f0;
      margin: 8px 0;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .page-enter {
      animation: fadeUp 0.3s ease forwards;
    }
  `}</style>
);

// ─────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────
const NavIcon = ({ name, color }) => {
  const common = {
    className: "nav-icon",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: "1.7",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <path d="M14 17.5h7M17.5 14v7" />
        </svg>
      );

    case "packages":
      return (
        <svg {...common}>
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      );

    case "batches":
      return (
        <svg {...common}>
          <path d="M4 6h16M4 10h16M4 14h10" />
          <circle cx="18" cy="16" r="3" />
          <path d="M18 13v3l2 1" />
        </svg>
      );

    case "investors":
      return (
        <svg {...common}>
          <path d="M3 17l4-8 4 4 3-5 4 9" />
          <path d="M3 21h18" />
        </svg>
      );

    case "inventory":
      return (
        <svg {...common}>
          <path d="M5 4h14a1 1 0 011 1v3H4V5a1 1 0 011-1z" />
          <path d="M4 8v12a1 1 0 001 1h14a1 1 0 001-1V8" />
          <path d="M10 12h4" />
        </svg>
      );

    case "tasks":
      return (
        <svg {...common}>
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
        </svg>
      );

    case "feeding":
      return (
        <svg {...common}>
          <path d="M12 20c-4 0-7-2.686-7-6h14c0 3.314-3 6-7 6z" />
          <path d="M8 10c0-2.21 1.79-4 4-4s4 1.79 4 4" />
          <path d="M12 6V3M9 8.5L7 6.5M15 8.5l2-2" />
        </svg>
      );

    case "vaccines":
      return (
        <svg {...common}>
          <path d="M9 3h6M12 3v3M8 6l8 8M16 6l-8 8M9 14l-5 5" />
          <circle cx="12" cy="18" r="2" />
        </svg>
      );

    case "calendar":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
          <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
        </svg>
      );

    case "medicine":
      return (
        <svg {...common}>
          <path d="M7 11l4.5-4.5a3.182 3.182 0 014.5 4.5L11.5 15.5" />
          <path d="M10 14l-5.5 5.5a2 2 0 002.829 2.829L12.83 16.8" />
          <path d="M9 13l2.5 2.5" />
        </svg>
      );

    case "disease":
      return (
        <svg {...common}>
          <path d="M12 2l7.5 4.5v5C19.5 16 16 20 12 21.5 8 20 4.5 16 4.5 11.5v-5L12 2z" />
          <path d="M12 8v5" />
          <circle cx="12" cy="15.5" r="0.75" fill={color} stroke="none" />
        </svg>
      );

    case "email":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
          <path d="M8 11l-5 6M16 11l5 6" />
        </svg>
      );

    case "setup":
      return (
        <svg {...common}>
          <path d="M12 22c5-4 8-8 8-12a8 8 0 10-16 0c0 4 3 8 8 12z" />
          <path d="M12 14c2.5-1.6 4-3.5 4-5.5a4 4 0 10-8 0c0 2 1.5 3.9 4 5.5z" />
        </svg>
      );

    case "schedule":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
          <path d="M8 14h8M8 18h5" />
        </svg>
      );

    case "stock":
      return (
        <svg {...common}>
          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
          <path d="M3.3 7l8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
      );

    case "preparation":
      return (
        <svg {...common}>
          <path d="M8 2v7" />
          <path d="M16 2v7" />
          <path d="M5 9h14" />
          <path d="M6 22h12a2 2 0 002-2V9H4v11a2 2 0 002 2z" />
        </svg>
      );

    case "recommendation":
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      );

    case "labour":
      return (
        <svg {...common}>
          <circle cx="12" cy="7" r="3.5" />
          <path d="M5 21v-2a7 7 0 0114 0v2" />
        </svg>
      );

    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l3 2" />
        </svg>
      );
  }
};

const navIcon = (name) => (color) => <NavIcon name={name} color={color} />;

// ─────────────────────────────────────────────
// NAV CONFIG
// ─────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      {
        path: "/admin/dashboard",
        label: "Dashboard",
        color: "#a78bfa",
        pill: "linear-gradient(120deg,rgba(139,92,246,0.28),rgba(124,58,237,0.12))",
        icon: navIcon("dashboard"),
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        path: "/admin/packages",
        label: "Packages",
        color: "#22d3ee",
        pill: "linear-gradient(120deg,rgba(6,182,212,0.22),rgba(6,182,212,0.08))",
        icon: navIcon("packages"),
      },
      {
        path: "/admin/batches",
        label: "Batches",
        color: "#fbbf24",
        pill: "linear-gradient(120deg,rgba(245,158,11,0.22),rgba(245,158,11,0.08))",
        icon: navIcon("batches"),
      },
      {
        path: "/admin/investors",
        label: "Investors",
        color: "#34d399",
        pill: "linear-gradient(120deg,rgba(16,185,129,0.22),rgba(16,185,129,0.08))",
        icon: navIcon("investors"),
      },
      {
        path: "/admin/inventory",
        label: "Inventory",
        color: "#60a5fa",
        pill: "linear-gradient(120deg,rgba(59,130,246,0.22),rgba(59,130,246,0.08))",
        icon: navIcon("inventory"),
      },
      {
        path: "/admin/tasks",
        label: "Tasks",
        color: "#f472b6",
        pill: "linear-gradient(120deg,rgba(236,72,153,0.22),rgba(236,72,153,0.08))",
        icon: navIcon("tasks"),
      },
    ],
  },
  {
    label: "Animal Care",
    items: [
      {
        path: "/admin/farm-care-updates",
        label: "Farm Care Updates",
        color: "#34d399",
        pill: "linear-gradient(120deg,rgba(52,211,153,0.24),rgba(16,185,129,0.08))",
        icon: navIcon("medicine"),
      },
      {
        path: "/admin/goat-labour",
        label: "Goat Labour",
        color: "#16a34a",
        pill: "linear-gradient(120deg,rgba(22,163,74,0.22),rgba(22,163,74,0.07))",
        icon: navIcon("labour"),
      },
      {
        path: "/admin/feeding",
        label: "Feeding",
        color: "#fb923c",
        pill: "linear-gradient(120deg,rgba(249,115,22,0.22),rgba(249,115,22,0.08))",
        icon: navIcon("feeding"),
      },
      {
        path: "/admin/vaccines",
        label: "Vaccines",
        color: "#2dd4bf",
        pill: "linear-gradient(120deg,rgba(20,184,166,0.22),rgba(20,184,166,0.08))",
        icon: navIcon("vaccines"),
      },
      {
        path: "/admin/vaccine-schedule",
        label: "Vaccine Schedule",
        color: "#818cf8",
        pill: "linear-gradient(120deg,rgba(99,102,241,0.22),rgba(99,102,241,0.08))",
        icon: navIcon("calendar"),
      },
      {
        path: "/admin/medicines",
        label: "Medicine Setup",
        color: "#fb7185",
        pill: "linear-gradient(120deg,rgba(244,63,94,0.22),rgba(244,63,94,0.08))",
        icon: navIcon("medicine"),
      },
      {
        path: "/admin/diseases",
        label: "Health & Treatment",
        color: "#f87171",
        pill: "linear-gradient(120deg,rgba(239,68,68,0.22),rgba(239,68,68,0.08))",
        icon: navIcon("disease"),
      },
      {
        path: "/admin/email-reminders",
        label: "Email Reminder",
        color: "#38bdf8",
        pill: "linear-gradient(120deg,rgba(56,189,248,0.24),rgba(14,165,233,0.08))",
        icon: navIcon("email"),
      },
    ],
  },
  {
    label: "Farming",
    items: [
      {
        path: "/admin/farming/setup",
        matchPaths: [
          "/admin/farming/setup",
          "/admin/farming/fertilizers",
          "/admin/farming/zones",
        ],
        label: "Setup",
        color: "#22c55e",
        pill: "linear-gradient(120deg,rgba(34,197,94,0.24),rgba(34,197,94,0.08))",
        icon: navIcon("setup"),
      },
      {
        path: "/admin/farming/schedules",
        label: "Schedule",
        color: "#8b5cf6",
        pill: "linear-gradient(120deg,rgba(139,92,246,0.24),rgba(139,92,246,0.08))",
        icon: navIcon("schedule"),
      },
      {
        path: "/admin/farming/tasks",
        label: "Today's Work",
        color: "#ec4899",
        pill: "linear-gradient(120deg,rgba(236,72,153,0.24),rgba(236,72,153,0.08))",
        icon: navIcon("tasks"),
      },
      {
        path: "/admin/farming/stock",
        label: "Stock",
        color: "#3b82f6",
        pill: "linear-gradient(120deg,rgba(59,130,246,0.24),rgba(59,130,246,0.08))",
        icon: navIcon("stock"),
      },
    ],
  },
  {
    label: "HR",
    items: [
      {
        path: "/admin/labour",
        label: "Labour",
        color: "#a3e635",
        pill: "linear-gradient(120deg,rgba(132,204,22,0.22),rgba(132,204,22,0.08))",
        icon: navIcon("labour"),
      },
    ],
  },
];

// ─────────────────────────────────────────────
// ADMIN LAYOUT
// ─────────────────────────────────────────────
const AdminLayout = ({ children }) => {
  const location = useLocation();
  const [showAdvancedFarm, setShowAdvancedFarm] = React.useState(false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const isItemMatch = (item, pathname) => {
    const paths = [item.path, ...(item.matchPaths || [])];

    return paths.some((path) => {
      if (path === "/admin/dashboard") return pathname === path;
      return pathname === path || pathname.startsWith(`${path}/`);
    });
  };

  const isActive = (item) => isItemMatch(item, location.pathname);

  const advancedFarmActive = NAV_SECTIONS.some(
    (section) =>
      section.advanced &&
      section.items.some((item) => isItemMatch(item, location.pathname))
  );

  return (
    <>
      <GlobalStyles />
      <div className={`agriwealth-admin${mobileNavOpen ? " nav-open" : ""}`} style={{ display: "flex", minHeight: "100vh" }}>
        <button
          className="farm-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
        />
        <aside className="farm-sidebar"
          style={{
            width: "var(--sidebar-w)",
            background: "var(--bg-sidebar)",
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            zIndex: 100,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-60px",
              left: "-60px",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle,rgba(139,92,246,0.18) 0%,transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "40px",
              right: "-40px",
              width: "160px",
              height: "160px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle,rgba(16,185,129,0.14) 0%,transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <div
            className="farm-brand"
            style={{
              padding: "20px 16px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              className="farm-brand-mark"
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background: "linear-gradient(135deg,#16856b,#0b9c7a)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 18px rgba(124,58,237,0.45)",
                flexShrink: 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2C6 2 3 8 3 14c0 4 2.5 7 6 8" />
                <path d="M12 2c6 0 9 6 9 12 0 4-2.5 7-6 8" />
                <path d="M12 22V10" />
                <path d="M7 14l5-4 5 4" />
              </svg>
            </div>

            <div className="farm-brand-copy">
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: 800,
                  color: "#172334",
                  letterSpacing: "-0.02em",
                }}
              >
                AgriWealth
              </div>
              <div
                style={{
                  fontSize: "9.5px",
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.28)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginTop: "2px",
                }}
              >
                Farm OS
              </div>
            </div>
          </div>

          <nav
            style={{
              flex: 1,
              padding: "6px 10px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {NAV_SECTIONS.map((section, si) => {
              const isAdvancedSection = section.advanced === true;
              const shouldShowSection =
                !isAdvancedSection || showAdvancedFarm || advancedFarmActive;

              if (isAdvancedSection && !shouldShowSection) {
                return (
                  <div key={section.label}>
                    <div className="nav-divider" />

                    <button
                      type="button"
                      onClick={() => setShowAdvancedFarm(true)}
                      style={{
                        width: "100%",
                        margin: "8px 0 4px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: "1px dashed rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.025)",
                        color: "rgba(255,255,255,0.38)",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        textAlign: "left",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <span>Advanced Farming</span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                        }}
                      >
                        Show
                      </span>
                    </button>
                  </div>
                );
              }

              return (
                <div key={section.label}>
                  <div
                    className="nav-section"
                    style={
                      isAdvancedSection
                        ? {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }
                        : undefined
                    }
                  >
                    <span>{section.label}</span>

                    {isAdvancedSection && (
                      <button
                        type="button"
                        onClick={() => setShowAdvancedFarm(false)}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "rgba(255,255,255,0.28)",
                          fontSize: "10px",
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "none",
                          letterSpacing: 0,
                        }}
                      >
                        Hide
                      </button>
                    )}
                  </div>

                  {section.items.map((item) => {
                    const active = isActive(item);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-link${active ? " active" : ""}`}
                        style={{ color: active ? item.color : undefined }}
                      >
                        <div className="nav-pill" style={{ background: item.pill }} />
                        {item.icon(item.color)}
                        <span className="nav-label">{item.label}</span>
                      </Link>
                    );
                  })}

                  {si < NAV_SECTIONS.length - 1 && <div className="nav-divider" />}
                </div>
              );
            })}
          </nav>

          <div
            className="farm-user-card"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.06)",
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "9px",
                flexShrink: 0,
                background: "linear-gradient(135deg,#16856b,#166f9e)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 800,
                color: "#fff",
              }}
            >
              A
            </div>
            <div className="farm-user-copy">
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                Admin
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.28)",
                  marginTop: "1px",
                }}
              >
                Super Admin
              </div>
            </div>
            <div
              style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: "#10b981",
                boxShadow: "0 0 8px #10b981",
                marginLeft: "auto",
                flexShrink: 0,
              }}
            />
          </div>
        </aside>

        <main
          className="page-enter farm-main"
          style={{
            marginLeft: "var(--sidebar-w)",
            width: "calc(100% - var(--sidebar-w))",
            minHeight: "100vh",
            background: "var(--bg-main)",
          }}
        >
          <div className="farm-topbar"
            style={{
              padding: "18px 28px 16px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "sticky",
              top: 0,
              zIndex: 50,
              background: "rgba(255,255,255,0.94)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="farm-topbar-actions" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                className="farm-mobile-menu"
                type="button"
                aria-label="Open navigation"
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(true)}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
              <button
                className="farm-notification-button"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "9px",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.4)",
                  transition: "all 0.18s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.09)";
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                  e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
              </button>

              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "9px",
                  background: "linear-gradient(135deg,#16856b,#0b9c7a)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: 800,
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 0 14px rgba(124,58,237,0.35)",
                }}
              >
                A
              </div>
            </div>
          </div>

          <div className="farm-page-content" style={{ padding: "28px" }}>{children}</div>
        </main>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// ROUTE GUARD
// ─────────────────────────────────────────────
const PrivateRoute = ({ children, role }) => {
  const token = localStorage.getItem("authToken");
  const userRole = localStorage.getItem("userRole");

  if (!token) {
    return <Navigate to={role === "admin" ? "/admin/login" : "/login"} />;
  }

  if (role === "admin" && userRole?.toUpperCase() !== "ADMIN") {
    return <Navigate to="/login" />;
  }

  return children;
};

const UserDashboard = () => (
  <div style={{ color: "#fff", padding: "20px" }}>
    <h1>User Dashboard</h1>
  </div>
);

const NotFound = () => (
  <div style={{ color: "#fff", padding: "60px", textAlign: "center" }}>
    <div
      style={{
        fontSize: "80px",
        fontWeight: 800,
        color: "rgba(255,255,255,0.06)",
      }}
    >
      404
    </div>
    <div
      style={{
        color: "rgba(255,255,255,0.3)",
        marginTop: "8px",
        fontSize: "14px",
      }}
    >
      Page not found
    </div>
  </div>
);

// ─────────────────────────────────────────────
// FARM SETUP HUB
// ─────────────────────────────────────────────
const SetupHubPage = () => {
  const cards = [
    { title: "1. Ingredient Stock", subtitle: "Add raw ingredients with quantity, unit, date, and minimum stock alert.", path: "/admin/farming/stock", color: "#3b82f6" },
    { title: "2. Fertilizers", subtitle: "Create fertilizer from stock ingredients. Ingredient stock reduces here.", path: "/admin/farming/fertilizers", color: "#22c55e" },
    { title: "3. Zones & Valves", subtitle: "Add zone, crop, plant count, then valve number, color, and valve plant count.", path: "/admin/farming/zones", color: "#06b6d4" },
    { title: "4. Schedule", subtitle: "Plan irrigation or fertilizer work for selected zone and valve.", path: "/admin/farming/schedules", color: "#8b5cf6" },
    { title: "5. Today's Work", subtitle: "Generate daily works and mark Yes/No. Fertilizer stock reduces on Yes.", path: "/admin/farming/tasks", color: "#ec4899" },
  ];
  return (
    <div
      className="farm-setup-hub"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0b1220 0%, #111827 100%)",
        padding: "24px",
        color: "#f9fafb",
        fontFamily:
          "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        className="setup-flow-head"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "22px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800 }}>
            Farm Setup
          </h1>
          <p
            style={{
              margin: "8px 0 0",
              color: "#94a3b8",
              fontSize: "14px",
              lineHeight: 1.6,
              maxWidth: "760px",
            }}
          >
            Use the simplified farm flow: Stock → Fertilizer → Zone/Valve → Schedule → Today's Work.
          </p>
        </div>
      </div>

      <div
        className="setup-flow-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "18px",
          maxWidth: "900px",
        }}
      >
        {cards.map((card) => (
          <Link
            key={card.path}
            to={card.path}
            className="setup-flow-card"
            style={{
              textDecoration: "none",
              color: "#f9fafb",
              background: "rgba(17, 24, 39, 0.96)",
              border: "1px solid #243041",
              borderRadius: "20px",
              padding: "22px",
              boxShadow: "0 14px 34px rgba(0,0,0,0.28)",
              transition: "0.18s ease",
              display: "block",
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "14px",
                background: `${card.color}22`,
                border: `1px solid ${card.color}55`,
                color: card.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                marginBottom: "16px",
              }}
            >
              →
            </div>

            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 800 }}>
              {card.title}
            </h2>

            <p
              style={{
                margin: "8px 0 0",
                color: "#94a3b8",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              {card.subtitle}
            </p>
          </Link>
        ))}
      </div>

      <div
        className="setup-flow-note"
        style={{
          marginTop: "22px",
          maxWidth: "900px",
          padding: "16px 18px",
          borderRadius: "16px",
          background: "rgba(34,197,94,0.10)",
          border: "1px solid rgba(34,197,94,0.22)",
          color: "#bbf7d0",
          fontSize: "13px",
          lineHeight: 1.6,
        }}
      >
        Recommended flow: add ingredient stock first, create fertilizer using stock, create zones and valves, add schedule, then generate Today's Work.
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// ADMIN ROUTES
// ─────────────────────────────────────────────
const ADMIN_ROUTES = [
  { path: "/admin/dashboard", el: <AdminDashboardPage /> },
  { path: "/admin/packages", el: <PackageManagementPage /> },
  { path: "/admin/batches", el: <BatchManagementPage /> },
  { path: "/admin/batches/:batchId/expenses", el: <BatchExpensesPage /> },
  { path: "/admin/batches/:batchId/livestock", el: <LivestockPage /> },
  { path: "/admin/investors", el: <InvestorManagementPage /> },
  { path: "/admin/inventory", el: <InventoryManagementPage /> },
  { path: "/admin/tasks", el: <TaskPage /> },
  { path: "/admin/feeding", el: <FeedingManagementPage /> },
  { path: "/admin/vaccines", el: <VaccineManagementPage /> },
  { path: "/admin/vaccine-schedule", el: <VaccineSchedulePage /> },
  { path: "/admin/medicines", el: <MedicineManagementPage /> },
  { path: "/admin/diseases", el: <DiseaseManagementPage /> },
  { path: "/admin/email-reminders", el: <EmailReminderPage /> },
  { path: "/admin/farm-care-updates", el: <FarmCareUpdatesPage /> },
  { path: "/admin/labour", el: <LabourManagementPage /> },
  { path: "/admin/goat-labour", el: <GoatLabourPage /> },

  // -------------------- Farming Routes --------------------
  { path: "/admin/farming", el: <Navigate to="/admin/farming/setup" replace /> },
  { path: "/admin/farming/setup", el: <SetupHubPage /> },

  // Setup sub-pages
  { path: "/admin/farming/fertilizers", el: <FertilizerMasterPage /> },
  { path: "/admin/farming/zones", el: <ZoneMasterPage /> },

  // Main simplified flow
  { path: "/admin/farming/schedules", el: <SchedulePage /> },
  { path: "/admin/farming/tasks", el: <FarmingTaskPage /> },
  { path: "/admin/farming/stock", el: <StockPage /> },

];

// ─────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────
export default function App() {
  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="colored" limit={4} />
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/login" element={<UserLoginPage />} />
        <Route path="/register" element={<UserRegisterPage />} />

        {ADMIN_ROUTES.map(({ path, el }) => (
          <Route
            key={path}
            path={path}
            element={
              <PrivateRoute role="admin">
                <AdminLayout>{el}</AdminLayout>
              </PrivateRoute>
            }
          />
        ))}

        <Route
          path="/dashboard"
          element={
            <PrivateRoute role="user">
              <UserDashboard />
            </PrivateRoute>
          }
        />

        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}
